import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from './../../../core/service/base/data.service';
import { APP_CONFIG, IAppConfig } from './../../../app.config';
import { Observable } from 'rxjs';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { MessageService } from '../../../core/service/message.service';
import { Company } from '../../model/company';
import { Property } from '../../model/property';
import { finalize, catchError, map } from 'rxjs/operators';
import { VacantRentalObjectDwelling } from '../../model/vacant-rental-object-dwelling';
import { Building } from '../../model/building';
import { RentalObjectType } from '../../model/rental-object-type';
import { VacantRentalObject } from '../../model/vacant-rental-object';
import { LocalDate } from '../../../core/model/local-date';

@Injectable()
export class VacantDwellingService extends DataService<VacantRentalObjectDwelling> {
  private readonly _defaultFilterFields: string[] = [
    'company',
    'property',
    'building',
    'rentalObjectType',
    'street',
    'rentalId',
    'vacantPeriod',
    'blockedRentalObjects',
    'displayType',
  ];

  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, VacantRentalObjectDwelling, progressBar, 'vacant-dwelling');
  }

  getFilterDefinition(): Observable<{
    company: string[];
    companies: Company[];
    property: string[];
    properties: Property[];
    building: string[];
    buildings: Building[];
    rentalObjectType: string;
    rentalObjectTypes: RentalObjectType[];
    street: string;
    city: string;
    rentalId: string;
    vacantPeriod: number;
    blockedRentalObjects: number;
    displayType: number;
    lettingStatus: number;
    selectedFields: string[];
    defaultFields: string[];
    vacantOnDate: LocalDate;
    companyCustomPropertiesTextValues: { key: string; value: string }[];
    companyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    companyCustomPropertiesNumericValues: { key: string; value: string }[];
    companyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
    rentalObjectCustomPropertiesTextValues: { key: string; value: string }[];
    rentalObjectCustomPropertiesDateValues: { key: string; value: LocalDate }[];
    rentalObjectCustomPropertiesNumericValues: { key: string; value: string }[];
    rentalObjectCustomPropertiesBooleanValues: { key: string; value: boolean }[];
  }> {
    return this.client.get<any>(`${this.endpoint}/filter`).pipe(
      map(res => {
        return {
          company: Array.isArray(res.company) ? res.company : res.company ? [res.company] : [],
          companies: Array.isArray(res.companies) ? res.companies.map(x => Object.assign(new Company(), x)) : [],
          property: Array.isArray(res.property) ? res.property : res.property ? [res.property] : [],
          properties: Array.isArray(res.properties) ? res.properties.map(x => Object.assign(new Property(), x)) : [],
          building: Array.isArray(res.building) ? res.building : res.building ? [res.building] : [],
          buildings: Array.isArray(res.buildings) ? res.buildings.map(x => Object.assign(new Building(), x)) : [],
          rentalObjectType: res.rentalObjectType ? res.rentalObjectType : '',
          rentalObjectTypes: Array.isArray(res.rentalObjectTypes)
            ? res.rentalObjectTypes.map(x => Object.assign(new RentalObjectType(), x))
            : [],
          street: res.street ? res.street : '',
          city: res.city ? res.city : '',
          rentalId: res.rentalId ? res.rentalId : '',
          vacantPeriod: Number.isInteger(res.vacantPeriod) ? res.vacantPeriod : 0,
          lettingStatus: Number.isInteger(res.lettingStatus) ? res.lettingStatus : null,
          blockedRentalObjects: Number.isInteger(res.blockedRentalObjects) ? res.blockedRentalObjects : 0,
          displayType: Number.isInteger(res.displayType) ? res.displayType : 0,
          vacantOnDate: res.vacantOnDate,
          companyCustomPropertiesTextValues: Array.isArray(res.companyCustomPropertiesTextValues)
            ? res.companyCustomPropertiesTextValues.map(v => ({
              key: v.key,
              value: v.value,
            }))
            : [],
          companyCustomPropertiesNumericValues: Array.isArray(res.companyCustomPropertiesNumericValues)
            ? res.companyCustomPropertiesNumericValues.map(v => ({
              key: v.key,
              value: v.value,
            }))
            : [],
          companyCustomPropertiesDateValues: Array.isArray(res.companyCustomPropertiesDateValues)
            ? res.companyCustomPropertiesDateValues.map(v => ({
              key: v.key,
              value: v.value,
            }))
            : [],
          companyCustomPropertiesBooleanValues: Array.isArray(res.companyCustomPropertiesBooleanValues)
            ? res.companyCustomPropertiesBooleanValues.map(v => ({
              key: v.key,
              value: v.value,
            }))
            : [],
          rentalObjectCustomPropertiesTextValues: Array.isArray(res.rentalObjectCustomPropertiesTextValues)
            ? res.rentalObjectCustomPropertiesTextValues.map(v => ({
              key: v.key,
              value: v.value,
            }))
            : [],
          rentalObjectCustomPropertiesDateValues: Array.isArray(res.rentalObjectCustomPropertiesDateValues)
            ? res.rentalObjectCustomPropertiesDateValues.map(v => ({
              key: v.key,
              value: v.value,
            }))
            : [],
          rentalObjectCustomPropertiesNumericValues: Array.isArray(res.rentalObjectCustomPropertiesNumericValues)
            ? res.rentalObjectCustomPropertiesNumericValues.map(v => ({
              key: v.key,
              value: v.value,
            }))
            : [],
          rentalObjectCustomPropertiesBooleanValues: Array.isArray(res.rentalObjectCustomPropertiesBooleanValues)
            ? res.rentalObjectCustomPropertiesBooleanValues.map(v => ({
              key: v.key,
              value: v.value,
            }))
            : [],
          selectedFields: Array.isArray(res.selectedFields) ? res.selectedFields : this._defaultFilterFields,
          defaultFields: this._defaultFilterFields,
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  getById(id: string): Observable<VacantRentalObject[]> {
    this.progressBar.start();
    return this.client.get(`${this.endpoint}/${id}`).pipe(
      map(data => Object.assign(new VacantRentalObject(), data)),
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getVacancyOverview(filter: any): Observable<any[]> {
    filter = this.prepareFilter(filter);
    this.progressBar.start();
    return this.client
      .post<any[]>(`${this.endpoint}/vacancy-overview`, JSON.stringify(filter), { headers: { 'Content-Type': 'application/json' } })
      .pipe(
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  export(ids: string[]): Observable<File> {
    return super.exportDataItems(ids, 'Lediga hyresobjekt bostad.xlsx');
  }
}
